/**
 * Maven version ordering, following `org.apache.maven.artifact.versioning.ComparableVersion`
 * closely enough to decide an affected-version range correctly.
 *
 * This exists because OSV states affectedness as ranges and the analysis must
 * resolve them the way Maven itself would. Getting this wrong is not a
 * cosmetic bug: under Maven's rules `2.9.1` sorts *before* `2.13.0`, so a
 * naive numeric comparator would silently clear genuinely affected releases
 * and report them as safe.
 */

const QUALIFIER_ORDER = new Map<string, number>([
  ["alpha", 1],
  ["a", 1],
  ["beta", 2],
  ["b", 2],
  ["milestone", 3],
  ["m", 3],
  ["rc", 4],
  ["cr", 4],
  ["snapshot", 5],
  ["", 6],
  ["ga", 6],
  ["final", 6],
  ["release", 6],
  ["sp", 7],
]);

/** Index of the release qualifier, used when a list runs out mid-comparison. */
const RELEASE_INDEX = 6;
/** Known qualifiers sort below unknown ones, which then compare lexically. */
const UNKNOWN_BASE = 8;

type Item =
  | { kind: "number"; value: number }
  | { kind: "string"; qualifier: string }
  | { kind: "list"; items: Item[] };

const ZERO: Item = { kind: "number", value: 0 };

function qualifierRank(qualifier: string): number {
  const known = QUALIFIER_ORDER.get(qualifier);
  if (known !== undefined) return known;
  return UNKNOWN_BASE;
}

function isNullItem(item: Item): boolean {
  if (item.kind === "number") return item.value === 0;
  if (item.kind === "string") return item.qualifier === "";
  if (!item.items.length) return true;
  return item.items.every(isNullItem);
}

function trimTrailingNulls(items: Item[]): Item[] {
  let end = items.length;
  while (end > 0 && isNullItem(items[end - 1]!)) end -= 1;
  return items.slice(0, end);
}

function parseItem(token: string, isDigit: boolean): Item {
  if (isDigit) {
    const digitsOnly = /^\d+$/.test(token);
    return {
      kind: "number",
      value: digitsOnly ? Number.parseInt(token, 10) : 0,
    };
  }
  return { kind: "string", qualifier: token.toLowerCase() };
}

/**
 * Mirrors ComparableVersion's tokenizer. A `.` adds an item to the current
 * list; a `-` adds an item *and pushes a nested list*, which is why `1.0-1`
 * sorts before `1.0.1` — the third element is a list against a number, and
 * ComparableVersion orders a list below a number.
 */
export function parseMavenVersion(version: string): Item {
  const normalized = version.trim().toLowerCase();
  if (!normalized) return { kind: "list", items: [ZERO] };

  const root: Item[] = [];
  let current: Item[] = root;
  const stack: Item[][] = [];
  let isDigit = false;
  let start = 0;

  const push = (item: Item) => current.push(item);
  const startNested = () => {
    const nested: Item[] = [];
    current.push({ kind: "list", items: nested });
    stack.push(current);
    current = nested;
  };

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index]!;
    if (char === ".") {
      push(index === start ? ZERO : parseItem(normalized.slice(start, index), isDigit));
      start = index + 1;
    } else if (char === "-") {
      push(index === start ? ZERO : parseItem(normalized.slice(start, index), isDigit));
      start = index + 1;
      startNested();
    } else if (char >= "0" && char <= "9") {
      if (!isDigit && index > start) {
        push({ kind: "string", qualifier: normalized.slice(start, index) });
        start = index;
        startNested();
      }
      isDigit = true;
    } else {
      if (isDigit && index > start) {
        push(parseItem(normalized.slice(start, index), true));
        start = index;
        startNested();
      }
      isDigit = false;
    }
  }
  if (start < normalized.length) {
    push(parseItem(normalized.slice(start), isDigit));
  } else if (start > 0 && normalized[start - 1] === ".") {
    push(ZERO);
  }
  while (stack.length) current = stack.pop()!;

  const trimmed = trimTrailingNulls(root);
  return { kind: "list", items: trimmed.length ? trimmed : [ZERO] };
}

function compareStringQualifier(left: string, right: string): number {
  const leftRank = qualifierRank(left);
  const rightRank = qualifierRank(right);
  if (leftRank !== rightRank) return leftRank < rightRank ? -1 : 1;
  if (leftRank >= UNKNOWN_BASE) {
    return left < right ? -1 : left > right ? 1 : 0;
  }
  return 0;
}

/** ComparableVersion compares a present item against "nothing" in a typed way. */
function compareToMissing(item: Item): number {
  if (item.kind === "number") return item.value === 0 ? 0 : 1;
  if (item.kind === "string") {
    const rank = qualifierRank(item.qualifier);
    return rank === RELEASE_INDEX ? 0 : rank < RELEASE_INDEX ? -1 : 1;
  }
  if (!item.items.length) return 0;
  return compareToMissing(item.items[0]!);
}

function compareItem(left: Item, right: Item): number {
  if (left.kind === "number" && right.kind === "number") {
    return left.value === right.value ? 0 : left.value < right.value ? -1 : 1;
  }
  if (left.kind === "string" && right.kind === "string") {
    return compareStringQualifier(left.qualifier, right.qualifier);
  }
  if (left.kind === "list" && right.kind === "list") {
    const length = Math.max(left.items.length, right.items.length);
    for (let index = 0; index < length; index += 1) {
      const a = left.items[index];
      const b = right.items[index];
      // A missing item compares as zero, which is a tie rather than a verdict,
      // so the walk has to continue past it. Treating it as the end of the
      // comparison is what made 1.0.1 and 1.0 look equal.
      const result = a && b ? compareItem(a, b) : a ? compareToMissing(a) : b ? -1 * compareToMissing(b) : 0;
      if (result !== 0) return result;
    }
    return 0;
  }
  // Mixed kinds. ComparableVersion's Item.Type ordering puts a string below a
  // list and a list below a number, which is what makes `1.0-1` (a nested
  // list in third position) sort below `1.0.1`.
  const rank = (item: Item) =>
    item.kind === "string" ? 0 : item.kind === "list" ? 1 : 2;
  const leftRank = rank(left);
  const rightRank = rank(right);
  return leftRank === rightRank ? 0 : leftRank < rightRank ? -1 : 1;
}

export function compareMavenVersions(left: string, right: string): number {
  return compareItem(parseMavenVersion(left), parseMavenVersion(right));
}

/**
 * A version is only usable for matching if it parses into something with real
 * structure. OSV uses a leading zero in the `introduced` event of an advisory
 * that affects every release, and some records use placeholders like
 * "unknown"; neither is a version, and treating either as one would either
 * match everything or nothing.
 */
export function isUsableVersion(version: string): boolean {
  const trimmed = version.trim();
  if (!trimmed) return false;
  if (!/^[0-9A-Za-z.\-_+]+$/.test(trimmed)) return false;
  const parsed = parseMavenVersion(trimmed);
  const items = parsed.kind === "list" ? parsed.items : [];
  if (!items.length) return false;
  // A bare qualifier such as "unknown" carries no ordering information.
  return items.some(
    (item) =>
      item.kind === "number" ||
      (item.kind === "list" && item.items.length > 0),
  );
}
